import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

const STORAGE_KEY = 'acor-modo-escuro';
const EVT = 'acor:theme:applied';

function applyHtml(isDark: boolean) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', isDark);
  root.style.colorScheme = isDark ? 'dark' : 'light';
}

function readStored(): boolean | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'true') return true;
    if (v === 'false') return false;
  } catch {}
  return null;
}

function systemPref(): boolean {
  return typeof window !== 'undefined'
    && !!window.matchMedia
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function useDarkMode(): [boolean, () => void] {
  const [isDark, setIsDark] = useState<boolean>(() => {
    const stored = readStored();
    return stored ?? systemPref();
  });

  // Aplica imediatamente e persiste
  useLayoutEffect(() => {
    applyHtml(isDark);
    try { localStorage.setItem(STORAGE_KEY, String(isDark)); } catch {}
    // avisa outras instâncias no mesmo documento
    try { window.dispatchEvent(new CustomEvent(EVT, { detail: { isDark } })); } catch {}
  }, [isDark]);

  // Sincroniza entre abas
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue != null) {
        setIsDark(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Sincroniza entre múltiplos hooks no MESMO app (outras árvores)
  useEffect(() => {
    const onApplied = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.isDark === 'boolean') setIsDark(detail.isDark);
    };
    window.addEventListener(EVT, onApplied as EventListener);
    return () => window.removeEventListener(EVT, onApplied as EventListener);
  }, []);

  // Segue o SO apenas enquanto o usuário não escolheu explicitamente
  useEffect(() => {
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mql) return;
    if (readStored() !== null) return;
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  const toggle = useCallback(() => setIsDark(v => !v), []);
  return [isDark, toggle];
}